import type { PriceItemType } from 'core/accounting/domain/PriceItemType'
import { type FullMode } from 'core/domain/Mode'
import type { StopType } from 'core/domain/Stop'

import type { RateLine } from './RateLine'

export type QuoteAccessorial = {
  name: string
  code: string
  stopType: Array<StopType>
}

export type AccessorialCharge = {
  rate: number
  unit: string
  notes?: string
  accessorial?: {
    code: string
    name: string
  }
  priceItemType?: Pick<PriceItemType, 'uuid' | 'label'>
  stopType?: StopType
}

export type RawAccessorialCharge = Pick<
  AccessorialCharge,
  'rate' | 'unit' | 'notes' | 'accessorial'
> & {
  price_item_type?: Pick<PriceItemType, 'uuid' | 'label'>
  stop_type?: StopType
}

export type AccessorialHistoryCharge = Omit<AccessorialCharge, 'accessorial'> & {
  accessorial?: {
    name: string
    code: string
    stopType: Array<StopType>
    modes: Array<FullMode>
  }
  priceItemType?: Pick<PriceItemType, 'uuid' | 'label'>
  stopType?: StopType
}

export function mapQuoteAccessorialToAccessorialCharge({
  quoteAccessorial,
  isSubmitted,
  prefilledAccessorial,
  accessorialRateLine,
}: {
  quoteAccessorial: QuoteAccessorial
  isSubmitted?: boolean
  prefilledAccessorial?: AccessorialHistoryCharge
  accessorialRateLine?: RateLine
}): AccessorialCharge {
  const accessorialCommonFields = {
    accessorial: {
      code: quoteAccessorial.code,
      name: quoteAccessorial.name,
    },
  }

  if (isSubmitted) {
    return {
      rate: accessorialRateLine?.total ?? 0,
      unit: 'once',
      notes: accessorialRateLine?.notes ?? '',
      ...accessorialCommonFields,
    }
  }

  return {
    rate: prefilledAccessorial?.rate ?? 0,
    unit: prefilledAccessorial?.unit ?? 'once',
    notes: prefilledAccessorial?.notes ?? '',
    ...accessorialCommonFields,
  }
}

export function getAllInRate(
  accessorialCharges: Array<AccessorialCharge> = [],
  flatRate: number = 0
): number {
  const validatedFlatRate = isNaN(flatRate) ? 0 : flatRate
  const accessorialSubTotal = accessorialCharges.reduce(
    (acumSum, accessorial) => acumSum + Number(accessorial.rate),
    0
  )
  return accessorialSubTotal + validatedFlatRate
}

export function hasAccessorialCharges(accessorialCharges?: Array<AccessorialCharge>) {
  return Boolean(accessorialCharges && accessorialCharges.length > 0)
}

export function getAccessorialCodes(accessorialCharges?: Array<AccessorialCharge>) {
  return (accessorialCharges ?? []).map(accessorial => accessorial.accessorial?.code)
}

export function getAccessorialCharges({
  ratelines,
  accessorialsHistory,
  quoteAccessorials,
  isSubmitted,
}: {
  ratelines: Array<RateLine>
  accessorialsHistory: Array<AccessorialHistoryCharge>
  quoteAccessorials?: Array<QuoteAccessorial>
  isSubmitted?: boolean
}): Array<AccessorialCharge> {
  if (!quoteAccessorials?.length) {
    return []
  }
  return quoteAccessorials.map(quoteAccessorial => {
    const prefilledAccessorial = accessorialsHistory.find(
      a => a.accessorial?.code === quoteAccessorial.code
    )
    const accessorialRateLine = ratelines.find(
      rateline => rateline.subtype === quoteAccessorial.code
    )

    return mapQuoteAccessorialToAccessorialCharge({
      quoteAccessorial,
      isSubmitted,
      prefilledAccessorial,
      accessorialRateLine,
    })
  })
}
